import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  FileCheck2, Layers, Landmark, ClipboardCheck, Save, RotateCcw, CheckCircle2, Clock, ListChecks,
  Send, ShieldCheck, XCircle, GitBranch,
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { fmtMoneyLabel } from '../../lib/format';
import { cn } from '../../lib/utils';
import { InvoicePlan } from './InvoicePlan';

const STATUS_OPTIONS = [
  { value: 'ATTACHED', label: 'Attached' },
  { value: 'NA', label: 'N/A' },
  { value: 'NOT_ATTACHED', label: 'Not Attached' },
];
const APPLICABILITY = [{ value: 'APPLICABLE', label: 'Applicable' }, { value: 'NA', label: 'N/A' }];

function SectionCard({ icon: Icon, title, hint, children, testid }) {
  return (
    <div className="erak-card space-y-4 p-5" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))]"><Icon size={17} /></span>
        <div><h3 className="font-display text-base font-semibold leading-tight">{title}</h3>{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>
      </div>
      {children}
    </div>
  );
}

export function CostCentreOpening({ projectId, currency = 'AED' }) {
  const [state, setState] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/opening`);
      setState(data);
      setForm({ ...data.opening });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load the opening form'); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  if (!state || !form) return <div className="h-64 animate-pulse rounded-2xl bg-muted/40" data-testid="cc-opening-loading" />;

  const { permissions, completeness, ccStatus, approval } = state;
  const opened = ccStatus === 'OPEN';
  const canEdit = permissions.canEdit && !opened;
  const money = (f) => fmtMoneyLabel(f, currency);
  const apprStatus = approval?.status || 'Draft';

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRow = (key, idx, patch) => setForm((f) => ({ ...f, [key]: f[key].map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ccNo: form.ccNo, startDate: form.startDate, endDate: form.endDate,
        checklist: form.checklist, financials: form.financials, scopeMatrix: form.scopeMatrix,
      };
      const { data } = await api.put(`/projects/${projectId}/opening`, payload);
      const wasVoided = (approval?.status === 'Approved' || approval?.status === 'Pending')
        && data.approval?.status === 'Draft';
      toast[wasVoided ? 'warning' : 'success'](wasVoided
        ? 'Saved — the previous approval was voided; re-submit for approval.'
        : 'Cost Centre Opening saved');
      setState((st) => ({ ...st, opening: data.opening, completeness: data.completeness, ready: data.ready, approval: data.approval,
        permissions: { ...st.permissions,
          canSubmit: st.permissions.canEdit && data.ready && ['Draft', 'Rejected'].includes(data.approval?.status),
          canOpenNow: st.permissions.canEdit && data.ready && data.approval?.status === 'Approved' } }));
      setForm({ ...data.opening });
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const submitForApproval = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/opening/submit`);
      toast.success('Submitted for approval');
      window.dispatchEvent(new Event('approvals:changed'));
      setState((st) => ({ ...st, approval: data.approval,
        permissions: { ...st.permissions, canSubmit: false } }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit for approval'); }
    finally { setSubmitting(false); }
  };

  const finByGroup = (g) => form.financials.map((f, i) => ({ ...f, _i: i })).filter((f) => f.group === g);

  return (
    <div className="space-y-5" data-testid="cost-centre-opening">
      <div className={cn('flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-4',
        opened ? 'border-[hsl(var(--success)/0.35)] bg-[hsl(var(--success)/0.08)]' : 'border-border bg-muted/30')}>
        <div className="flex items-center gap-2 text-sm">
          {opened ? <CheckCircle2 size={18} className="text-[hsl(var(--success))]" /> : <Clock size={18} className="text-[hsl(var(--warning))]" />}
          <span className="font-medium" data-testid="cc-status-label">
            {opened ? `Cost centre ${form.ccNo || ''} OPEN (opened by ${form.openedBy || '—'})` : 'AWARDED — cost centre not yet opened'}
          </span>
          {!opened && (
            apprStatus === 'Approved'
              ? <Badge variant="outline" className="border-[hsl(var(--success)/0.4)] text-[hsl(var(--success))]" data-testid="cc-ready-badge">Approved — ready to open</Badge>
              : completeness.complete
                ? <Badge variant="outline" className="border-[hsl(var(--brand)/0.4)] text-[hsl(var(--brand))]" data-testid="cc-complete-badge">Complete — {apprStatus === 'Pending' ? 'awaiting approval' : 'submit for approval'}</Badge>
                : <Badge variant="outline" className="border-[hsl(var(--warning)/0.4)] text-[hsl(var(--warning))]" data-testid="cc-incomplete-badge">{completeness.outstanding.length} item(s) outstanding</Badge>)}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="cc-reload"><RotateCcw size={14} className="mr-1.5" />Reset</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="cc-save-button"><Save size={14} className="mr-1.5" />{saving ? 'Saving…' : 'Save form'}</Button>
          </div>
        )}
      </div>

      {!opened && !completeness.complete && (
        <div className="erak-card p-4" data-testid="cc-outstanding-summary">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[hsl(var(--warning))]"><ListChecks size={15} />Outstanding to open the cost centre</div>
          <ul className="grid gap-1 sm:grid-cols-2">
            {completeness.outstanding.map((o, i) => (
              <li key={o} data-testid={`cc-outstanding-${i}`} className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--warning))]" />{o}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Part 4 — Cost Centre Opening approval gate */}
      {!opened && (
        <div className="erak-card space-y-4 p-5" data-testid="cc-approval-panel">
          {(() => {
            const META = {
              Draft: { tone: 'muted-foreground', icon: ListChecks, label: 'Draft — not yet submitted' },
              Pending: { tone: 'warning', icon: Clock, label: 'Awaiting approval' },
              Approved: { tone: 'success', icon: CheckCircle2, label: 'Approved — ready to open' },
              Rejected: { tone: 'danger', icon: XCircle, label: 'Rejected — revise and re-submit' },
            };
            const meta = META[apprStatus] || META.Draft;
            const MetaIcon = meta.icon;
            const stages = approval?.workflow || [];
            const curIdx = approval?.stageIndex ?? 0;
            return (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-xl', `bg-[hsl(var(--${meta.tone})/0.12)] text-[hsl(var(--${meta.tone}))]`)}><ShieldCheck size={17} /></span>
                    <div>
                      <h3 className="font-display text-base font-semibold leading-tight">Opening Approval</h3>
                      <div className="flex items-center gap-1.5 text-xs" data-testid="cc-approval-status">
                        <MetaIcon size={13} className={`text-[hsl(var(--${meta.tone}))]`} />
                        <span className={`font-medium text-[hsl(var(--${meta.tone}))]`}>{meta.label}</span>
                        {apprStatus === 'Pending' && approval?.stageLabel && (
                          <Badge variant="outline" className="ml-1 text-[10px]" data-testid="cc-approval-stage">Pending {approval.stageLabel}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  {permissions.canSubmit && (
                    <Button size="sm" onClick={submitForApproval} disabled={submitting || !completeness.complete}
                      data-testid="cc-submit-approval-button"
                      title={!completeness.complete ? 'Complete the form first' : ''}>
                      <Send size={14} className="mr-1.5" />{submitting ? 'Submitting…' : 'Submit for approval'}
                    </Button>
                  )}
                </div>

                {apprStatus === 'Rejected' && approval?.rejectedReason && (
                  <div className="rounded-lg border border-[hsl(var(--danger)/0.35)] bg-[hsl(var(--danger)/0.06)] p-3 text-xs" data-testid="cc-approval-reject-reason">
                    <span className="font-semibold text-[hsl(var(--danger))]">Rejection reason: </span>{approval.rejectedReason}
                  </div>
                )}

                {stages.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2" data-testid="cc-approval-chain">
                    {stages.map((s, i) => {
                      const done = apprStatus === 'Approved' || i < curIdx;
                      const current = apprStatus === 'Pending' && i === curIdx;
                      const initiator = i === 0;
                      return (
                        <React.Fragment key={s.key || i}>
                          {i > 0 && <GitBranch size={12} className="rotate-90 text-muted-foreground" />}
                          <Badge variant="outline"
                            className={cn('gap-1 text-[10px]',
                              done && 'border-[hsl(var(--success)/0.4)] text-[hsl(var(--success))]',
                              current && 'border-[hsl(var(--warning)/0.5)] text-[hsl(var(--warning))]')}
                            data-testid={`cc-approval-chain-stage-${i}`}>
                            {done && <CheckCircle2 size={9} />}{current && <Clock size={9} />}
                            {s.label}{initiator ? ' (initiator)' : ''}
                          </Badge>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}

                {(approval?.decisions || []).length > 0 && (
                  <div className="space-y-1 border-t pt-3" data-testid="cc-approval-history">
                    {approval.decisions.map((d) => (
                      <div key={`${d.stageKey}-${d.at}`} className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                        <span className={cn('font-medium', d.decision === 'Rejected' ? 'text-[hsl(var(--danger))]' : 'text-[hsl(var(--success))]')}>{d.decision}</span>
                        <span>· {d.stageLabel}</span>
                        <span>· {d.by}</span>
                        {d.override && <Badge variant="secondary" className="text-[9px] text-[hsl(var(--warning))]">override</Badge>}
                        {d.note && <span className="italic">“{d.note}”</span>}
                      </div>
                    ))}
                  </div>
                )}

                {apprStatus === 'Draft' && !completeness.complete && (
                  <p className="text-[11px] text-muted-foreground">Submit for approval unlocks once every section is addressed and the invoice plan is valid.</p>
                )}
                {apprStatus === 'Pending' && (
                  <p className="text-[11px] text-muted-foreground">Editing the form or invoice plan now will void this approval and require a re-submission.</p>
                )}
              </>
            );
          })()}
        </div>
      )}

      <SectionCard icon={Landmark} title="Project & Commercial" hint="CC No (unique, locks on open) and dates. Contract Value & Budget are read-only from the won tender." testid="cc-section-commercial">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label>CC No</Label>
            <Input data-testid="cc-no-input" value={form.ccNo || ''} disabled={!canEdit} placeholder="e.g. EPS-1057" onChange={(e) => setField('ccNo', e.target.value)} className="font-num" />
          </div>
          <div className="space-y-1.5">
            <Label>Project Start Date</Label>
            <Input type="date" data-testid="cc-start-date" value={form.startDate || ''} disabled={!canEdit} onChange={(e) => setField('startDate', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Project End Date</Label>
            <Input type="date" data-testid="cc-end-date" value={form.endDate || ''} disabled={!canEdit} onChange={(e) => setField('endDate', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Contract Value ({currency})</Label>
            <div className="flex h-10 items-center rounded-md border bg-muted/40 px-3 font-num text-sm" data-testid="cc-contract-value-ro">{money(form.contractValue)}</div>
          </div>
          <div className="space-y-1.5">
            <Label>Contract Budget ({currency})</Label>
            <div className="flex h-10 items-center rounded-md border bg-muted/40 px-3 font-num text-sm" data-testid="cc-budget-ro">{money(form.budget)}</div>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={FileCheck2} title="Document Checklist" hint="Mark each document Attached, N/A or Not Attached — every item must be addressed." testid="cc-section-checklist">
        <div className="grid gap-2">
          {form.checklist.map((c, i) => (
            <div key={c.item} data-testid={`cc-checklist-row-${i}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2">
              <span className="text-sm">{c.item}</span>
              <Select value={c.status || undefined} disabled={!canEdit} onValueChange={(v) => setRow('checklist', i, { status: v })}>
                <SelectTrigger className={cn('h-8 w-40', !c.status && 'border-[hsl(var(--warning)/0.5)]')} data-testid={`cc-checklist-status-${i}`}><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard icon={ClipboardCheck} title="Financial Requirements" hint="Guarantees, cheques and insurance — mark Applicable/N/A; Applicable rows need %, amount, date and responsibilities." testid="cc-section-financials">
        {['GUARANTEE', 'CHEQUE', 'INSURANCE'].map((grp) => (
          <div key={grp} className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{grp}</div>
            {finByGroup(grp).map((f) => {
              const on = f.applicability === 'APPLICABLE';
              return (
                <div key={f.type} data-testid={`cc-fin-row-${f._i}`} className="grid items-center gap-2 rounded-lg border px-3 py-2 md:grid-cols-12">
                  <div className="flex items-center gap-2 md:col-span-3">
                    <Select value={f.applicability || undefined} disabled={!canEdit} onValueChange={(v) => setRow('financials', f._i, { applicability: v })}>
                      <SelectTrigger className={cn('h-8 w-28', !f.applicability && 'border-[hsl(var(--warning)/0.5)]')} data-testid={`cc-fin-applicability-${f._i}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{APPLICABILITY.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-sm">{f.type}</span>
                  </div>
                  <Input type="number" step="0.01" placeholder="%" className="h-8 font-num md:col-span-1" disabled={!canEdit || !on} value={f.pct ?? ''} onChange={(e) => setRow('financials', f._i, { pct: e.target.value })} data-testid={`cc-fin-pct-${f._i}`} />
                  <Input type="number" step="0.01" placeholder="Amount" className="h-8 font-num md:col-span-2" disabled={!canEdit || !on} value={on ? (f.amount ?? '') : ''} onChange={(e) => setRow('financials', f._i, { amount: e.target.value })} data-testid={`cc-fin-amount-${f._i}`} />
                  <Input type="date" className="h-8 md:col-span-2" disabled={!canEdit || !on} value={f.requiredDate || ''} onChange={(e) => setRow('financials', f._i, { requiredDate: e.target.value })} data-testid={`cc-fin-date-${f._i}`} />
                  <Input placeholder="Facilitator" className="h-8 md:col-span-2" disabled={!canEdit || !on} value={f.facilitator || ''} onChange={(e) => setRow('financials', f._i, { facilitator: e.target.value })} data-testid={`cc-fin-facilitator-${f._i}`} />
                  <Input placeholder="Collect back" className="h-8 md:col-span-2" disabled={!canEdit || !on} value={f.collector || ''} onChange={(e) => setRow('financials', f._i, { collector: e.target.value })} data-testid={`cc-fin-collector-${f._i}`} />
                </div>
              );
            })}
          </div>
        ))}
      </SectionCard>

      <SectionCard icon={Layers} title="Scope Matrix" hint="Mark each scope Applicable or N/A." testid="cc-section-scope">
        <div className="grid gap-2 sm:grid-cols-2">
          {form.scopeMatrix.map((s, i) => (
            <div key={s.scope} data-testid={`cc-scope-row-${i}`} className="flex flex-col gap-2 rounded-lg border px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm">{s.scope}</span>
                <Select value={s.applicability || undefined} disabled={!canEdit} onValueChange={(v) => setRow('scopeMatrix', i, { applicability: v })}>
                  <SelectTrigger className={cn('h-8 w-28', !s.applicability && 'border-[hsl(var(--warning)/0.5)]')} data-testid={`cc-scope-applicability-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{APPLICABILITY.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {s.freeText && s.applicability === 'APPLICABLE' && (
                <Textarea rows={2} placeholder="Details…" disabled={!canEdit} value={s.note || ''} onChange={(e) => setRow('scopeMatrix', i, { note: e.target.value })} data-testid={`cc-scope-note-${i}`} />
              )}
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard icon={ClipboardCheck} title="Invoice Plan" hint="Part of the Cost Centre Opening — must total 100% and be date-valid to open." testid="cc-section-invoice">
        <InvoicePlan projectId={projectId} currency={currency} embedded onChanged={load} />
      </SectionCard>
    </div>
  );
}
